import { Button } from '@/components/ui/button';
import Modal from '@/components/ui/modal';
import type { CountryIsoCode } from '@/graphql/graphql';
import { useTranslations } from 'next-intl';
import CustomCheckBox from './custom-checkbox';
import { ModalMode } from './excluded-countries-modal';

type RestrictionType = '' | 'classic' | 'FAST' | 'local_vod';
type ZoneKey = 'europ' | 'apac' | 'indianSub' | 'latinAmerica' | 'mena' | 'ssa';
type ZoneLabelKey = `ScreeningRoom.modal.restrictedCountry.zone.${ZoneKey}`;

interface RestrictionTypeModalProps {
    isOpen?: ModalMode;
    onClose: () => void;
    isRestrictionZoneChoice: boolean;
    selectedZoneCode: string;
    selectedCountryCode: string;
    currentRestrictionType: RestrictionType[];
    onRestrictionTypeChange: (type: RestrictionType) => void;
    onApply: () => void;
}

export default function RestrictionTypeModal({
    isOpen,
    onClose,
    isRestrictionZoneChoice,
    selectedZoneCode,
    selectedCountryCode,
    currentRestrictionType,
    onRestrictionTypeChange,
    onApply,
}: RestrictionTypeModalProps) {
    const t = useTranslations();
    const tc = useTranslations('IsoCountry');

    return (
        <Modal
            closePosition="top-[20px] right-[20px]"
            bg="bg-[#0E0E0E]"
            isOpen={!!isOpen}
            onClose={onClose}
            ModalSize="max-w-[545px]"
            modalPadding="p-[30px]"
        >
            <div className="flex flex-col items-center gap-6 mt-[20px]">
                <div className="text-center text-[24px] font-semibold">
                    {t.rich('ScreeningRoom.modal.restrictedCountry.modalTitle', {
                        highlight: (chunks: React.ReactNode) => <b className="text-[#DD06AE]">{chunks}</b>,
                        name: isRestrictionZoneChoice
                            ? t(`ScreeningRoom.modal.restrictedCountry.zone.${selectedZoneCode}` as ZoneLabelKey)
                            : selectedCountryCode
                              ? tc(selectedCountryCode as CountryIsoCode)
                              : '',
                    })}
                </div>

                <div className="flex flex-col gap-3 max-w-[410px] w-full">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={currentRestrictionType.includes('classic')}
                            onChange={() => onRestrictionTypeChange('classic')}
                            className="cursor-pointer hidden peer"
                        />
                        <CustomCheckBox />
                        <span>{t('ScreeningRoom.modal.exclude.classic')}</span>
                    </label>

                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={currentRestrictionType.includes('FAST')}
                            onChange={() => onRestrictionTypeChange('FAST')}
                            className="cursor-pointer hidden peer"
                        />
                        <CustomCheckBox />
                        <span>{t('ScreeningRoom.modal.exclude.fast')}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={currentRestrictionType.includes('local_vod')}
                            onChange={() => onRestrictionTypeChange('local_vod')}
                            className="cursor-pointer hidden peer"
                        />
                        <CustomCheckBox />
                        <span>{t('ScreeningRoom.modal.exclude.local_vod')}</span>
                    </label>

                    <label className="flex items-center cursor-pointer">
                        <input type="checkbox" checked={true} disabled className="cursor-pointer hidden peer" />
                        <CustomCheckBox />
                        <span className="text-[#656565] cursor-not-allowed">
                            {t('ScreeningRoom.modal.exclude.other')}
                        </span>
                    </label>
                </div>

                <Button
                    onClick={onApply}
                    className="px-[30px] py-[15px] h-auto
                    self-center text-white rounded-[8px]
                    cursor-pointer bg-[#DD06AE] hover:bg-[#B10DCE]
                    transition-all duration-300
                    disabled:bg-[#656565] disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={currentRestrictionType.length === 0}
                >
                    {t('ScreeningRoom.modal.restrictedCountry.confirm')}
                </Button>
            </div>
        </Modal>
    );
}
